<template>
  <BaseModal
    :is-visible="showModal"
    title="Request a withdrawal"
    @close="closeModal"
  >
    <template #default>
      <div
        v-if="!haveError && !haveRequest && !haveSuccess"
        :class="$style['input-group']"
      >
        <label for="amountWithdrawal">
          Amount
          <br />
          <span :class="$style['info-message']">
            Amount available for withdrawal {{ formatToUSD(currentTotalAmount) }}
          </span>
        </label>

        <Money3Component
          v-model="amountWithdrawal"
          v-bind="configInput"
          :class="{ [$style['error']]: inputError }"
          @input="inputError = false"
        />

        <span
          v-if="inputError"
          :class="$style['error-message']"
        >
          {{ errorMessage }}
        </span>
      </div>

      <div v-if="haveRequest">
        <div style="display: flex; justify-content: center; margin-bottom: 15px;">
          <LoadingSpinner />
        </div>
      </div>

      <div v-if="haveError">
        <div :class="[$style['box-message'], $style['error']]">
          <ExclamationTriangleIcon :class="$style.icon" />
          <span>
            We encountered an internal issue.<br />
            Please try again later.
          </span>
        </div>
      </div>

      <div v-if="haveSuccess">
        <div :class="[$style['box-message'], $style['success']]">
          <CheckCircleIcon :class="$style.icon" />
          <span>Withdrawal request successfully created!</span>
        </div>
      </div>
    </template>

    <template #footer v-if="!haveError && !haveRequest && !haveSuccess">
      <button @click="createWithdrawal" :class="$style['btn-create']">Request</button>
    </template>
  </BaseModal>
</template>

<script setup>
  import { ref, watch, computed } from 'vue';
  import { useStore } from 'vuex';
  import { Money3Component } from 'v-money3';
  import { formatToUSD } from '@/helpers/currencyFormatter';
  import api from '@/axios';
  import BaseModal from '@/components/common/BaseModal.vue';
  import LoadingSpinner from '@/components/LoadingSpinner.vue';
  import {
    ExclamationTriangleIcon,
    CheckCircleIcon
  } from '@heroicons/vue/24/outline';
import { onMounted } from 'vue';

  const props = defineProps({
    showModal: {
      type: Boolean,
      default: false,
    },
  });

  const store = useStore();
  const emit = defineEmits(['close']);

  const amountWithdrawal = ref(0);
  const currentTotalAmount = ref(0);
  const inputError = ref(false);
  const errorMessage = ref('Invalid amount');

  const request = ref({
    pending: false,
    success: false,
    error: false
  });

  const configInput = ref({
    masked: false,
    prefix: '$',
    suffix: '',
    thousands: ',',
    decimal: '.',
    precision: 2,
    disableNegative: true,
    disabled: false,
    min: null,
    max: null,
    allowBlank: false,
    minimumNumberOfCharacters: 0,
    shouldRound: false,
    focusOnRight: false,
  });

  const haveSuccess = computed(() => request.value.success);
  const haveError = computed(() => request.value.error);
  const haveRequest = computed(() => request.value.pending);

  function closeModal() {
    store.dispatch('modals/hideModalWithdrwal');
    emit('close');
  }

  function setErrorMessage(type = 1) {
    errorMessage.value =
      type === 2
        ? `The maximum amount is ${formatToUSD(currentTotalAmount.value)}`
        : 'Invalid amount';
  }

  async function createWithdrawal() {
    if (amountWithdrawal.value <= 0) {
      inputError.value = true;
      setErrorMessage();
      return;
    }

    if (amountWithdrawal.value > currentTotalAmount.value) {
      inputError.value = true;
      setErrorMessage(2);
      return;
    }

    request.value.pending = true;

    try {
      const token = store.state.auth.token;

      const response = await api.post(
        '/withdraws/create/',
        { amount: amountWithdrawal.value },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response?.status === 201) {
        request.value.success = true;
      } else {
        request.value.error = true;
      }
    } catch {
      request.value.error = true;
    }

    request.value.pending = false;
  }

  async function getCurrentAmount() {
    request.value.pending = true;

    try {
      const token = store.state.auth.token;
      const response = await api.get('/max_withdraw_value/', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response?.data?.max_withdraw) {
        currentTotalAmount.value = response.data.max_withdraw;
        configInput.value.max = Math.trunc(currentTotalAmount.value * 100) / 100;
      } else {
        request.value.error = true;
      }
    } catch {
      request.value.error = true;
    }

    request.value.pending = false;
  }

  async function initialize() {
    request.value = { pending: false, success: false, error: false };
    amountWithdrawal.value = 0;
    await getCurrentAmount();
  }

  onMounted(async () => {
    await initialize()
  });

  watch(() => props.showModal, (nv) => {
    if (nv) initialize();
  });
</script>


<style lang="scss" module>

  .input-group {
    gap: 15px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    input {
      padding: 15px;
      font-size: 16px;
      color: #C1C1C1;
      border-radius: 8px;
      width: calc(100% - 30px);
      border: 1px solid #535353;
      background: rgba(217, 217, 217, 0.05);

      &.error {
        color: #E64545;
        border: 1px solid #E64545;
      }
    }
  }

  .btn-create {
    border: unset;
    font-size: 16px;
    cursor: pointer;
    color: #232323;
    font-weight: 400;
    line-height: 26px;
    padding: 6px 16px;
    border-radius: 8px;    
    font-style: normal;
    margin-bottom: 5px;
    background: #BBF90A;
    font-family: $font-secondary;

    &:hover {
      color: #232323;
      background: #90C300;
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  .error-message {
    color: #E64545;
    font-size: 12px;
  }

  .info-message {
    font-size: 12px;
  }

  .box-message {
    gap: 20px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    .icon {
      width: 60px;
    }

    &.error {
      color:#E64545;
      
      .icon {
        color:#E64545
      }
    }

    &.success {
      color:#7CF95C;
      
      .icon {
        color:#7CF95C
      }
    }
  }

</style>
